// Settings — QuickBooks connection lifecycle. Status card for every grantee;
// Connect / Disconnect only for connection managers (canManageConnection).
// Also the consumer of the OAuth callback (pendingQboCallback from App.jsx).
// Below it, the Bank data card (statement uploads, Financials v2 slice 1).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { C, font } from "../../../lib/styles.js";
import notify from "../../../lib/notify.jsx";
import { qboDisconnect, qboExchangeCode, qboStartConnect } from "../../../lib/qboClient.js";
import { clearQboCache } from "../lib/useQbo.js";
import BankDataCard from "./BankDataCard.jsx";
import { Badge, lblStyle, panel } from "./common.jsx";

const kv = { display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 8, fontSize: 12.5 };
const kStyle = { color: C.muted };
const btn = {
  font: "inherit", fontSize: 12, padding: "7px 14px", borderRadius: 6,
  border: `1px solid ${C.border}`, background: C.surface, cursor: "pointer",
};
const primaryBtn = { ...btn, background: C.text, color: "#fff", borderColor: C.text };
const dangerBtn = { ...btn, color: C.danger, borderColor: C.danger };

const fmtWhen = (ms) => (ms ? new Date(ms).toLocaleDateString("en-US",
  { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function Settings({ ctx, qbo, refreshStatus, pendingQboCallback, onQboCallbackConsumed }) {
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [exchange, setExchange] = useState(null); // null | "working" | "done" | "cancelled" | error msg
  const consumedRef = useRef(false);

  // Consume the OAuth callback exactly once, after the gate is open.
  useEffect(() => {
    if (!pendingQboCallback || consumedRef.current || !ctx.unlockToken) return;
    consumedRef.current = true;
    if (pendingQboCallback.error) {
      setExchange("cancelled");
      onQboCallbackConsumed();
      return;
    }
    (async () => {
      setExchange("working");
      try {
        await qboExchangeCode({
          unlockToken: ctx.unlockToken,
          code: pendingQboCallback.code,
          realmId: pendingQboCallback.realmId,
          state: pendingQboCallback.state,
        });
        clearQboCache();
        setExchange("done");
        refreshStatus();
      } catch (e) {
        setExchange(e.message || "Connection failed");
      } finally {
        onQboCallbackConsumed();
      }
    })();
  }, [pendingQboCallback, ctx.unlockToken, onQboCallbackConsumed, refreshStatus]);

  const doConnect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { authorizeUrl } = await qboStartConnect({
        unlockToken: ctx.unlockToken,
        redirectOrigin: window.location.origin,
      });
      window.location.assign(authorizeUrl); // full-page redirect to Intuit
    } catch (e) {
      notify.error(e.message);
      setBusy(false);
    }
  };

  const doDisconnect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await qboDisconnect({ unlockToken: ctx.unlockToken });
      clearQboCache();
      setConfirmDisconnect(false);
      refreshStatus();
    } catch (e) {
      notify.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const canManage = qbo?.canManage === true;
  // After an upload or a mapping change: every cached read may be stale (the
  // Overview's and Expenses' bank lines, the lists behind them). The card
  // re-reads its own bank_status; the other tabs re-read when next opened.
  const onBankChanged = useCallback(() => clearQboCache(), []);

  return (
    <div style={{ fontFamily: font }}>
      {exchange === "working" && (
        <div style={{ ...panel, maxWidth: 520, marginBottom: 14, color: C.muted, fontSize: 13 }}>
          Completing the QuickBooks connection…
        </div>
      )}
      {exchange === "done" && (
        <div style={{ ...panel, maxWidth: 520, marginBottom: 14, color: "#047857", fontSize: 13 }}>
          QuickBooks connected.
        </div>
      )}
      {exchange === "cancelled" && (
        <div style={{ ...panel, maxWidth: 520, marginBottom: 14, color: C.muted, fontSize: 13 }}>
          The connection was cancelled at Intuit. Nothing changed.
        </div>
      )}
      {typeof exchange === "string" && !["working", "done", "cancelled"].includes(exchange) && (
        <div style={{ ...panel, maxWidth: 520, marginBottom: 14, color: C.danger, fontSize: 13 }}>
          {exchange}
        </div>
      )}

      <div style={{ ...panel, maxWidth: 520 }}>
        <span style={lblStyle}>QuickBooks connection</span>
        <div style={{ height: 1, background: C.border, margin: "10px 0 14px" }} />
        {!qbo?.connected ? (
          <>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>
              Not connected. {canManage ?
                "Connect the company file to light up the dashboard." :
                "Ask Ivin to connect the company file."}
            </div>
            {canManage && (
              <button style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }} onClick={doConnect} disabled={busy}>
                {busy ? "Opening Intuit…" : "Connect QuickBooks"}
              </button>
            )}
          </>
        ) : (
          <>
            <div style={kv}>
              <span style={kStyle}>Status</span>
              <span>Connected <Badge tone={qbo.env === "prod" ? "prod" : "sandbox"}>{qbo.env === "prod" ? "production" : "sandbox"}</Badge></span>
              <span style={kStyle}>Company</span>
              <span>{qbo.companyName || "—"}</span>
              <span style={kStyle}>Connected</span>
              <span>{fmtWhen(qbo.connectedAt)}</span>
              <span style={kStyle}>Token health</span>
              <span>
                {qbo.needsReconnect ?
                  <Badge tone="over">reconnect needed</Badge> :
                  <Badge tone="ok">good</Badge>}
                {qbo.refreshLastRotatedAt ? (
                  <span style={{ fontSize: 11.5, color: C.muted, marginLeft: 8 }}>
                    rotated {fmtWhen(qbo.refreshLastRotatedAt)}
                  </span>
                ) : null}
              </span>
            </div>
            {canManage && (
              <>
                <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
                {qbo.needsReconnect && (
                  <button style={{ ...primaryBtn, marginRight: 10, opacity: busy ? 0.6 : 1 }} onClick={doConnect} disabled={busy}>
                    Reconnect QuickBooks
                  </button>
                )}
                {!confirmDisconnect ? (
                  <button style={dangerBtn} onClick={() => setConfirmDisconnect(true)}>Disconnect QuickBooks</button>
                ) : (
                  <span>
                    <span style={{ fontSize: 12.5, color: C.muted, marginRight: 10 }}>Disconnect and drop the stored tokens?</span>
                    <button style={{ ...dangerBtn, marginRight: 8, opacity: busy ? 0.6 : 1 }} onClick={doDisconnect} disabled={busy}>
                      {busy ? "Disconnecting…" : "Yes, disconnect"}
                    </button>
                    <button style={btn} onClick={() => setConfirmDisconnect(false)}>Cancel</button>
                  </span>
                )}
              </>
            )}
          </>
        )}
      </div>

      <BankDataCard ctx={ctx} qbo={qbo} onChanged={onBankChanged} />
    </div>
  );
}
